import React, { useState, useEffect } from "react";
import {
  XMarkIcon,
  CheckIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
// 1. Ensure correct import name
import { getMemberList, addGroupMembers } from "@/store/slices/chatSlice";
import { useTranslation } from "react-i18next";

const AddMemberFlow = ({ chatId, currentMembers = [], onCancel, onSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('chat');
  const { member_list, member_list_pagination, member_list_loading } = useSelector(
    (state) => state.chat
  );

  const hasMore = member_list_pagination?.has_more ?? false;

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const existingMemberIds = currentMembers.map((m) => m?.user?.id);

  useEffect(() => {
    setPage(1);
    dispatch(getMemberList({ page: 1, search, reset: true }));
  }, [dispatch, search]);

  const fetchMoreMembers = () => {
    if (!hasMore || member_list_loading) return;
    const nextPage = page + 1;
    setPage(nextPage);
    dispatch(getMemberList({ page: nextPage, search }));
  };

  const toggleMember = (id) => {
    if (existingMemberIds.includes(id)) return;
    setSelectedMembers((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );
  };

  const handleAddMembers = async () => {
    if (selectedMembers.length === 0 || submitting) return;

    setSubmitting(true);

    const formData = new FormData();
    formData.append("group_id", chatId);
    selectedMembers.forEach((id) => {
      formData.append("member_ids[]", id);
    });

    try {
      const result = await dispatch(addGroupMembers(formData));
      
      // 2. FIX: Use 'addGroupMembers' here, not 'addMembers'
      if (addGroupMembers.fulfilled.match(result)) {
        // 3. This triggers the parent to switch views
        onSuccess?.(); 
      }
    } catch (error) {
      console.error("Failed to add members", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white relative">
      {/* Header */}
      <div className="p-4 flex items-center gap-2 border-b border-gray-100">
        <button onClick={onCancel} className="p-1 hover:bg-gray-100 rounded-full">
          <XMarkIcon className="h-6 w-6 text-gray-500" />
        </button>

        <div className="relative flex-grow">
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#333333DE]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('searchContacts')}
            className="w-full bg-[#0000000A] rounded-full py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-1 focus:ring-[#00a884] placeholder-[#736C64]"
            autoFocus
          />
          {search && <XMarkIcon className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-[#333333DE] cursor-pointer" onClick={() => setSearch('')} />}
          
        </div>
      </div>

      {/* Selected Members Preview */}
      {selectedMembers.length > 0 && (
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex gap-2 overflow-x-auto custom-scrollbar">
          {member_list.filter(u => selectedMembers.includes(u.user_id)).map(u => (
             <div key={u.user_id} className="flex flex-col items-center min-w-[60px]">
                <div className="relative">
                <img src={u.profile_image || "/img/icons/user-profile.png"} className="w-10 h-10 rounded-full object-cover" />
                  <div 
                    onClick={(e) => { e.stopPropagation(); toggleMember(u.user_id); }}
                    className="absolute -top-1 -right-1 bg-gray-500 hover:bg-red-500 text-white rounded-full p-0.5 cursor-pointer"
                  >
                  <XMarkIcon className="w-3 h-3 text-[#333333DE]" />
                  </div>
                </div>
                <span className="text-[10px] truncate w-full text-center mt-1">{u.first_name}</span>
             </div>
           ))}
        </div>
      )}

      {/* Member List */}
      <div id="membersScroll" className="flex-grow overflow-y-auto px-4 pb-24">
        <InfiniteScroll
          dataLength={member_list.length}
          next={fetchMoreMembers}
          hasMore={hasMore}
          scrollableTarget="membersScroll"
          loader={<div className="flex justify-center mb-4">
            <div className="h-6 w-6 border-2 border-gray-400 border-t-[#065C36] rounded-full animate-spin"></div>
          </div>}
          endMessage={
            <p className="text-center text-gray-400 py-6 text-sm">
              {member_list?.length === 0 ? t('noUserFound') : ""}
            </p>
          }
        >
          {member_list.map((u) => {
            const isAlreadyMember = existingMemberIds.includes(u.user_id);
            const isSelected = selectedMembers.includes(u.user_id);

            return (
              <div
                key={u.user_id}
                onClick={() => toggleMember(u.user_id)}
                className={`flex justify-between items-center p-3 rounded-xl cursor-pointer transition-colors ${
                    isAlreadyMember ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <img
                    src={u.profile_image || "/img/icons/user-profile.png"}
                    className="w-11 h-11 rounded-full object-cover border border-gray-100"
                  />
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold">
                      {u.full_name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {u.user_role}
                    </span>
                  </div>
                </div>

                {isAlreadyMember ? (
                   <span className="text-xs text-gray-400 font-medium px-2">{t('added')}</span>
                ) : (
                    <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                        isSelected
                        ? "bg-[#00a884] border-[#00a884]"
                        : "border-gray-300"
                    }`}
                    >
                    {isSelected && (
                        <CheckIcon className="h-4 w-4 text-white stroke-[3]" />
                    )}
                    </div>
                )}
              </div>
            );
          })}
        </InfiniteScroll>
      </div>

      {/* Submit Button */}
      <div className="absolute bottom-6 right-6 z-10">
        <button
          disabled={!selectedMembers.length || submitting}
          onClick={handleAddMembers}
          className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all transform hover:scale-105 active:scale-95 ${
            selectedMembers.length && !submitting
              ? "bg-[#00a884] text-white hover:bg-[#008f6f]"
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
          }`}
        >
          {submitting ? (
             <div className="w-6 h-6 border-2 border-gray-400 border-t-white rounded-full animate-spin"></div>
          ) : (
             <CheckIcon className="h-7 w-7" />
          )}
        </button>
      </div>
    </div>
  );
};

export default AddMemberFlow;